import React, { useEffect, useMemo, useState } from "react";
import { Divider } from "@mui/material";
import FilterItem from "../../components/Filter/FilterItem";
import { RootState } from "../../store/store";
import { useDispatch, useSelector } from "react-redux";
import {
  setFilterResult,
  resetFilterResult,
} from "../../store/slices/filterSlice";

import classes from "./FilterList.module.scss";
import { DateRangeLabels } from "../../utils/constants";
import { DateRange } from "../../type/enums";

const isFilterEmpty = (filter: any) =>
  [
    "selectedAffiliates",
    "selectedCountries",
    "selectedEvaluation",
    "selectedRegions",
    "selectedProduct",
    "selectedRisk",
    "selectedSampleId",
    "selectedStatus",
    "selectedFieldName",
  ].every((key) => filter?.[key]?.length === 0);

interface FilterListProps {
  disabled?: boolean;
  hideClear?: boolean;
}

const FilterItems = ({
  selectedFilter,
  refData,
  disabled,
  handleDelete,
  handleDeleteAll,
}: any) => (
  <div className={classes.filter}>
    {selectedFilter.dateRangeText && (
      <FilterItem
        name={DateRangeLabels[selectedFilter.dateRangeText as DateRange]}
        disabled={disabled}
      />
    )}

    {selectedFilter.reviewType && (
      <FilterItem
        name={selectedFilter.reviewType?.join(", ")}
        disabled={disabled}
      />
    )}

    {selectedFilter.selectedRegions?.map((option: any) => (
      <FilterItem
        key={option}
        name={option}
        onClose={() => handleDelete(option, "selectedRegions")}
        disabled={disabled}
      />
    ))}

    {selectedFilter.selectedAffiliates?.map((option: any) => (
      <FilterItem
        key={option}
        name={option}
        onClose={() => handleDelete(option, "selectedAffiliates")}
        disabled={disabled}
      />
    ))}

    <EvaluationItems
      selectedFilter={selectedFilter}
      refData={refData}
      disabled={disabled}
      handleDelete={handleDelete}
      handleDeleteAll={handleDeleteAll}
    />

    {selectedFilter?.selectedCountries?.map((option: string) => (
      <FilterItem
        key={option}
        name={option}
        onClose={() => handleDelete(option, "selectedCountries")}
        disabled={disabled}
      />
    ))}

    {selectedFilter.selectedRisk?.map((option: any, index: number) => (
      <FilterItem
        key={`risk-${index}`}
        name={option}
        onClose={() => handleDelete(option, "selectedRisk")}
        disabled={disabled}
      />
    ))}

    <StatusItems
      selectedFilter={selectedFilter}
      refData={refData}
      disabled={disabled}
      handleDelete={handleDelete}
      handleDeleteAll={handleDeleteAll}
    />

    {selectedFilter.selectedProduct?.map((option: any) => (
      <FilterItem
        key={option}
        name={option}
        onClose={() => handleDelete(option, "selectedProduct")}
        disabled={disabled}
      />
    ))}

    {selectedFilter.selectedSampleId?.map((option: any) => (
      <FilterItem
        key={option}
        name={option}
        onClose={() => handleDelete(option, "selectedSampleId")}
        disabled={disabled}
      />
    ))}

    {selectedFilter.selectedFieldName?.map((option: any) => (
      <FilterItem
        key={option}
        name={option}
        onClose={() => handleDelete(option, "selectedFieldName")}
        disabled={disabled}
      />
    ))}
  </div>
);

const EvaluationItems = ({
  selectedFilter,
  refData,
  disabled,
  handleDelete,
  handleDeleteAll,
}: any) =>
  selectedFilter?.selectedEvaluation?.some(
    (evaluation: any) => evaluation.evaluation_type_id === "all",
  ) ? (
    <FilterItem
      name={refData?.["text-sentinel-ref-evaluation-type"]
        ?.map((option: any) => option.evaluation_type)
        .join(", ")}
      onClose={() => handleDeleteAll("selectedEvaluation")}
      disabled={disabled}
    />
  ) : (
    selectedFilter?.selectedEvaluation?.map((option: any, index: number) => (
      <FilterItem
        key={`eval-${index}`}
        name={option}
        onClose={() => handleDelete(option, "selectedEvaluation")}
        disabled={disabled}
      />
    ))
  );

const StatusItems = ({
  selectedFilter,
  refData,
  disabled,
  handleDelete,
  handleDeleteAll,
}: any) =>
  selectedFilter?.selectedStatus?.some(
    (status: any) => status.status_id === "all",
  ) ? (
    <FilterItem
      name={refData?.["text-sentinel-ref-status"]
        ?.map((option: any) => option.record_status)
        .join(", ")}
      onClose={() => handleDeleteAll("selectedStatus")}
      disabled={disabled}
    />
  ) : (
    selectedFilter.selectedStatus?.map((option: any) => (
      <FilterItem
        key={`status-${option}`}
        name={option}
        onClose={() => handleDelete(option, "selectedStatus")}
        disabled={disabled}
      />
    ))
  );

const FilterList: React.FC<FilterListProps> = (props) => {
  const { disabled, hideClear } = props;
  const dispatch = useDispatch();
  const { selectedFilter: globalFilter, refData } = useSelector(
    (state: RootState) => state.filter,
  );
  const [selectedFilter, setSelectedFilters] = useState(globalFilter);
  const [isChanged, setIsChanged] = useState<boolean>(false);

  useEffect(() => {
    setSelectedFilters(globalFilter);
  }, [globalFilter]);

  const handleDeleteAll = (filterKey: string) => {
    dispatch(setFilterResult({ ...selectedFilter, [filterKey]: [] }));
  };

  const handleDelete = async (option: any, filterKey: string) => {
    const updatedFilterItems = new Set(selectedFilter[filterKey] || []);

    if (updatedFilterItems.has(option)) {
      updatedFilterItems.delete(option);
    }
    setIsChanged(true);
    setSelectedFilters({
      ...selectedFilter,
      [filterKey]: Array.from(updatedFilterItems),
    });
  };

  const applyFilters = () => {
    setIsChanged(false);
    dispatch(setFilterResult(selectedFilter));
  };

  const handleclearAll = () => dispatch(resetFilterResult());

  const isClearAllDisabled = useMemo(
    () => isFilterEmpty(selectedFilter),
    [selectedFilter],
  );

  if (!selectedFilter) return null;

  return (
    <div
      className={`${classes["filter-bar"]} ${
        hideClear ? classes["hideClear"] : ""
      }`}
      data-testid="filterList"
    >
      <FilterItems
        selectedFilter={selectedFilter}
        refData={refData}
        disabled={disabled}
        handleDelete={handleDelete}
        handleDeleteAll={handleDeleteAll}
      />
      {!hideClear && (
        <div className={classes["filter-action"]}>
          <Divider
            orientation="vertical"
            variant="middle"
            flexItem
            className={classes.verticalDivider}
          />
          <div className={classes["reset-filters"]}>
            {isChanged && (
              <button
                className={classes["reset-button"]}
                onClick={applyFilters}
              >
                Apply filters
              </button>
            )}
            <button
              className={classes["reset-button"]}
              onClick={handleclearAll}
              disabled={isClearAllDisabled}
            >
              Clear all filters
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FilterList;
